import { io, Socket } from 'socket.io-client';
import { ACHIEVEMENT_DEFINITIONS, CANVAS_EXPANSION_CONFIG } from '@the-wall/shared';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
  PaletteColor,
  PixelPlacement,
  Comment,
} from '@the-wall/shared';
import { useCanvasStore } from '@/stores/canvasStore';
import { useAuthStore } from '@/stores/authStore';
import { useChatStore } from '@/stores/chatStore';
import { showSnackbar } from '@/stores/snackbarStore';
import { useBadgeStore } from '@/stores/badgeStore';

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: TypedSocket | null = null;
let disconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let normalPixelBatch: PixelPlacement[] = [];
let yoloPixelBatch: PixelPlacement[] = [];
let normalBatchTimeout: ReturnType<typeof setTimeout> | null = null;
let yoloBatchTimeout: ReturnType<typeof setTimeout> | null = null;

/** Callback for comment:new events (set by ArchiveDetail when viewing an archive). */
let onCommentNew: ((data: { archiveId: string; comment: Comment }) => void) | null = null;

export function setCommentNewHandler(
  handler: ((data: { archiveId: string; comment: Comment }) => void) | null
): void {
  onCommentNew = handler;
}

export function connectSocket(): TypedSocket {
  if (socket) {
    return socket;
  }

  const token = useAuthStore.getState().token;

  // Connect with or without token (guests have no token).
  // Use polling first so the first connection is more resilient (e.g. under Strict Mode
  // or slow networks); Socket.IO will upgrade to WebSocket after.
  socket = io({
    auth: token ? { token } : {},
    transports: ['polling', 'websocket'],
  });

  const store = useCanvasStore.getState();

  socket.on('connect', () => {
    console.log('Connected to server');
    useCanvasStore.setState({ isConnected: true });

    // Cancel pending disconnect notification if we reconnected quickly
    if (disconnectTimeout) {
      clearTimeout(disconnectTimeout);
      disconnectTimeout = null;
    } else {
      // Only show connected snackbar on fresh connections, not quick reconnects
      socket?.emit('canvas:request');
      showSnackbar('Connected to The Wall', 'success', 3000);
    }

    const activeUser = useAuthStore.getState().user;
    if (activeUser?.id && activeUser.selectedBadges) {
      useBadgeStore.getState().setBadgesForUser(activeUser.id, activeUser.selectedBadges);
    }
  });

  socket.on('disconnect', () => {
    console.log('Disconnected from server');
    useCanvasStore.setState({ isConnected: false });

    // Delay showing disconnect notification to avoid flashing during quick reconnects
    disconnectTimeout = setTimeout(() => {
      disconnectTimeout = null;
      // Only show if still disconnected
      if (!socket?.connected) {
        showSnackbar('Disconnected from server', 'error', 5000);
      }
    }, 2000);
  });

  socket.on('canvas:state', (data) => {
    console.log('Received canvas state');
    store.initializeCanvas(data.pixels, data.pixelMeta, data);
  });

  socket.on('pixel:update', (data) => {
    store.setPixel(data.x, data.y, data.color, {
      username: data.username,
      usernameColor: data.usernameColor ?? null,
      usernameEffect: data.usernameEffect ?? null,
      placedAt: data.placedAt,
      userId: data.userId,
    });
  });

  socket.on('pixel:batchUpdate', (data) => {
    for (const pixel of data.pixels) {
      store.setPixel(pixel.x, pixel.y, pixel.color, {
        username: pixel.username,
        usernameColor: pixel.usernameColor ?? null,
        usernameEffect: pixel.usernameEffect ?? null,
        placedAt: pixel.placedAt,
        userId: pixel.userId,
      });
    }
  });

  socket.on('canvas:expanded', (data) => {
    store.initializeCanvas(data.pixels, data.pixelMeta, {
      ...data,
      shiftOverlayBy: data.expandedByPixels,
    });
    store.showExpansionCelebration({
      previousWidth: data.previousWidth,
      previousHeight: data.previousHeight,
      width: data.width,
      height: data.height,
      expandedByPixels: data.expandedByPixels,
    });
  });

  socket.on('pixel:rejected', (data) => {
    console.warn('Pixel rejected:', data.reason);
    // Revert optimistic update by requesting fresh canvas state from server
    socket?.emit('canvas:request');
    // Update ink balance if provided
    if (data.inkBalance !== undefined) {
      useAuthStore.getState().updateInkBalance(data.inkBalance);
    }
    const duration = data.requiresVerification ? 8000 : 3000;
    showSnackbar(`Pixel rejected: ${data.reason}`, 'warning', duration);
  });

  socket.on('user:inkUpdate', (data) => {
    useAuthStore.getState().updateInkBalance(data.inkBalance);
  });

  socket.on('user:achievementUnlocked', (data) => {
    const achievement = ACHIEVEMENT_DEFINITIONS.find((item) => item.id === data.achievementId);
    const title = achievement?.title ?? 'Achievement';
    showSnackbar(`Achievement unlocked: ${title}`, 'success', 5000, {
      achievementId: data.achievementId,
    });
  });

  socket.on('canvas:reset', (data) => {
    console.log('Canvas reset for new day:', data.date);
    store.resetCanvas({
      width: data.width,
      height: data.height,
      expansionCount: data.expansionCount,
    });

    // Format the date nicely
    const date = new Date(data.date);
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });

    showSnackbar(
      `The Wall has been archived! The ${formattedDate} piece is now in the gallery. Fresh canvas ready.`,
      'success',
      8000
    );
  });

  socket.on('error', (data) => {
    console.error('Socket error:', data.message);
    showSnackbar(`Error: ${data.message}`, 'error', 5000);
  });

  // Admin events
  socket.on('admin:archiveSuccess', (data) => {
    showSnackbar(
      `✅ Canvas archived successfully! Archive ID: ${data.archiveId.slice(0, 8)}...`,
      'success',
      5000
    );
  });

  socket.on('admin:archiveFailed', (data) => {
    showSnackbar(`❌ Archive failed: ${data.reason}`, 'error', 5000);
  });

  socket.on('admin:expandSuccess', (data) => {
    showSnackbar(
      `Canvas expanded to ${data.width}x${data.height} (+${data.expandedByPixels}px each side)`,
      'success',
      5000
    );
  });

  socket.on('admin:expandFailed', (data) => {
    showSnackbar(`Expansion failed: ${data.reason}`, 'error', 5000);
  });

  socket.on('comment:new', (data) => {
    onCommentNew?.(data);
  });

  // Chat
  socket.on('chat:history', (data) => {
    useChatStore.getState().setMessages(data.messages);
  });

  socket.on('chat:message', (data) => {
    const store = useChatStore.getState();
    store.addMessage(data);
    // Also mark unread when user is on another tab (even if chat modal is open)
    if (typeof document !== 'undefined' && document.hidden) {
      store.setHasUnread(true);
    }
  });

  socket.on('chat:rejected', (data) => {
    showSnackbar(data.reason, 'warning', 3000);
  });

  socket.on('chat:messagesRemoved', (data) => {
    useChatStore.getState().removeMessages(data.messageIds);
  });

  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  normalPixelBatch = [];
  yoloPixelBatch = [];
  if (normalBatchTimeout) {
    clearTimeout(normalBatchTimeout);
    normalBatchTimeout = null;
  }
  if (yoloBatchTimeout) {
    clearTimeout(yoloBatchTimeout);
    yoloBatchTimeout = null;
  }
}

function flushPixelBatch(yolo: boolean): void {
  const batch = yolo ? yoloPixelBatch : normalPixelBatch;
  const timeout = yolo ? yoloBatchTimeout : normalBatchTimeout;
  if (timeout) {
    clearTimeout(timeout);
    if (yolo) {
      yoloBatchTimeout = null;
    } else {
      normalBatchTimeout = null;
    }
  }

  if (!socket?.connected || batch.length === 0) return;

  const pixels = batch.splice(0, CANVAS_EXPANSION_CONFIG.MAX_BATCH_SIZE);
  socket.emit('pixel:placeBatch', { pixels, ...(yolo ? { yolo: true } : {}) });

  if (batch.length > 0) {
    const nextTimeout = setTimeout(
      () => flushPixelBatch(yolo),
      CANVAS_EXPANSION_CONFIG.CLIENT_BATCH_DELAY_MS
    );
    if (yolo) {
      yoloBatchTimeout = nextTimeout;
    } else {
      normalBatchTimeout = nextTimeout;
    }
  }
}

function queuePixelPlacement(pixel: PixelPlacement, yolo: boolean): void {
  const batch = yolo ? yoloPixelBatch : normalPixelBatch;
  batch.push(pixel);

  if (batch.length >= CANVAS_EXPANSION_CONFIG.MAX_BATCH_SIZE) {
    flushPixelBatch(yolo);
    return;
  }

  const hasTimeout = yolo ? yoloBatchTimeout : normalBatchTimeout;
  if (hasTimeout) return;

  const timeout = setTimeout(
    () => flushPixelBatch(yolo),
    CANVAS_EXPANSION_CONFIG.CLIENT_BATCH_DELAY_MS
  );
  if (yolo) {
    yoloBatchTimeout = timeout;
  } else {
    normalBatchTimeout = timeout;
  }
}

export function placePixel(x: number, y: number, color: PaletteColor, yolo?: boolean): void {
  if (!socket?.connected) {
    console.error('Socket not connected');
    return;
  }

  queuePixelPlacement(
    { x, y, color, expansionCount: useCanvasStore.getState().expansionCount },
    Boolean(yolo)
  );
}

export function triggerAdminArchive(): void {
  if (!socket?.connected) {
    showSnackbar('Not connected to server', 'error', 3000);
    return;
  }

  socket.emit('admin:archive');
}

export function triggerAdminCanvasExpansion(): void {
  if (!socket?.connected) {
    showSnackbar('Not connected to server', 'error', 3000);
    return;
  }

  socket.emit('admin:expandCanvas');
}

export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}

export function getSocket(): TypedSocket | null {
  return socket;
}

export function sendChatMessage(content: string): void {
  if (!socket?.connected) {
    showSnackbar('Not connected to server', 'error', 3000);
    return;
  }
  const replyingTo = useChatStore.getState().replyingTo;
  socket.emit('chat:message', { content, replyToMessageId: replyingTo?.id ?? null });
  useChatStore.getState().clearReplyingTo();
}

export function deleteChatMessage(messageId: string): void {
  if (!socket?.connected) {
    showSnackbar('Not connected to server', 'error', 3000);
    return;
  }
  socket.emit('chat:deleteMessage', { messageId });
}
